import { ApplicationRef, Injectable, inject, isDevMode, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { concat, filter, first, interval, switchMap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
	private readonly appRef = inject(ApplicationRef);
	private readonly swUpdate = inject(SwUpdate);

	readonly updateReady = signal(false);
	private applying = false;

	initialize(): void {
		if (isDevMode() || !this.swUpdate.isEnabled) return;

		this.swUpdate.versionUpdates
			.pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
			.subscribe(() => {
				this.updateReady.set(true);
				/* Evita chunks antigos (MIME text/html) após deploy. */
				void this.applyUpdate();
			});

		this.swUpdate.unrecoverable.subscribe((event) => {
			console.error('[sw] Unrecoverable state:', event.reason);
			document.location.reload();
		});

		const appStable$ = this.appRef.isStable.pipe(first((stable) => stable));
		concat(appStable$, interval(6 * 60 * 60 * 1000))
			.pipe(switchMap(() => this.swUpdate.checkForUpdate()))
			.subscribe({
				error: (err) => console.error('[sw] checkForUpdate failed', err)
			});

		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') {
				void this.swUpdate.checkForUpdate().catch((err) => {
					console.error('[sw] checkForUpdate (visibility) failed', err);
				});
			}
		});
	}

	async applyUpdate(): Promise<void> {
		if (this.applying) return;
		this.applying = true;

		if (!this.swUpdate.isEnabled) {
			document.location.reload();
			return;
		}

		try {
			const activated = await this.swUpdate.activateUpdate();
			if (!activated) {
				console.warn('[sw] activateUpdate returned false; reloading anyway');
			}
		} catch (err) {
			console.error('[sw] activateUpdate failed', err);
		} finally {
			document.location.reload();
		}
	}
}
